import {
  Box,
  CircularProgress,
  Modal,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import SignalWifiOffOutlinedIcon from "@mui/icons-material/SignalWifiOffOutlined";
import type { NetworkStatus } from "components/store/networkStatusStore";

type NetworkStatusModalProps = {
  status: NetworkStatus;
};

export function NetworkStatusModal({ status }: NetworkStatusModalProps) {
  const theme = useTheme();
  const isOpen = status === "offline" || status === "server-unreachable";
  const isOffline = status === "offline";

  if (!isOpen) {
    return null;
  }

  const Icon = isOffline ? SignalWifiOffOutlinedIcon : CloudOffOutlinedIcon;
  const title = isOffline ? "You are offline" : "Server connection lost";
  const description = isOffline
    ? "Service Queue needs an internet connection. The application is paused until you are back online."
    : "Your internet is available, but Service Queue cannot reach the API server. The application is paused while we retry automatically.";

  return (
    <Modal
      open={isOpen}
      disableEscapeKeyDown
      hideBackdrop={false}
      aria-labelledby="network-status-title"
      aria-describedby="network-status-description"
      BackdropProps={{
        sx: {
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.common.black, 0.78)
              : alpha(theme.palette.common.black, 0.55),
          backdropFilter: "blur(4px)",
        },
      }}
      sx={{
        zIndex: theme.zIndex.modal + 2000,
      }}
    >
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          pointerEvents: "auto",
        }}
      >
        <Box
          role="alertdialog"
          aria-modal="true"
          sx={{
            width: "100%",
            maxWidth: 440,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.error.main, 0.28)}`,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 24px 64px rgba(0, 0, 0, 0.65)"
                : theme.shadows[24],
            px: 4,
            py: 4,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              mx: "auto",
              mb: 2.5,
              width: 80,
              height: 80,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.error.main, 0.12),
              boxShadow: `0 0 0 8px ${alpha(theme.palette.error.main, 0.06)}`,
            }}
          >
            <Icon
              sx={{
                fontSize: 42,
                color: theme.palette.error.main,
              }}
              aria-hidden="true"
            />
          </Box>

          <Typography
            id="network-status-title"
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 1.5 }}
          >
            {title}
          </Typography>

          <Typography
            id="network-status-description"
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}
          >
            {description}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{
              mt: 3,
              px: 2,
              py: 1.25,
              borderRadius: 1.5,
              bgcolor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.common.white, 0.06)
                  : alpha(theme.palette.common.black, 0.04),
            }}
          >
            {isOffline ? (
              <>
                <SignalWifiOffOutlinedIcon
                  fontSize="small"
                  color="disabled"
                  aria-hidden="true"
                />
                <Typography variant="caption" color="text.secondary">
                  Waiting for internet connection...
                </Typography>
              </>
            ) : (
              <>
                <CircularProgress size={16} thickness={5} aria-hidden="true" />
                <Typography variant="caption" color="text.secondary">
                  Retrying connection...
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
}
